import { sortBy } from 'lodash-es';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { atomFamily, atomWithRefresh, loadable } from 'jotai/utils';
import { getRegionsStoreCount, RegionCountsMap } from 'services/api/admin';
import { errorToasterWithServerMsg } from 'utils/errorToaster';

type AllPlatformRegionStoreCountState = {
  platforms: {
    [K in General.PlatformUpperCase]: {
      map: RegionCountsMap;
      array: Array<{ region: General.Region; count: number }>;
      total: number;
    };
  };
  totalStoresCount: number;
};

export const getPlatformRegionCountInfo = (regionCountsMap: RegionCountsMap) => {
  let totalAuth = 0;
  const countArray = Object.keys(regionCountsMap).map((key) => {
    const count = regionCountsMap[key] || 0;
    totalAuth += count;
    return {
      region: key.replace(/^(\w{2,})Cnt$/, '$1') as General.Region,
      count,
    };
  });

  return {
    totalAuth,
    countArray: sortBy(countArray, 'region'),
  };
};

const defaultState: AllPlatformRegionStoreCountState = {
  platforms: {
    SHOPEE: {
      map: {} as any,
      array: [],
      total: 0,
    },
    LAZADA: {
      map: {} as any,
      array: [],
      total: 0,
    },
    TOKOPEDIA: {
      map: {} as any,
      array: [],
      total: 0,
    },
    TIKTOK: {
      map: {} as any,
      array: [],
      total: 0,
    },
    ALIEXPRESS: {
      map: {} as any,
      array: [],
      total: 0,
    },
    TIKTOK_US: {
      map: {} as any,
      array: [],
      total: 0,
    },
    MERCADO: {
      map: {} as any,
      array: [],
      total: 0,
    },
    KWAI: {
      map: {} as any,
      array: [],
      total: 0,
    },
    FACEBOOK: {
      map: {} as any,
      array: [],
      total: 0,
    },
  },
  totalStoresCount: 0,
};
const getAllRegionCounts = (lastState?: AllPlatformRegionStoreCountState) =>
  getRegionsStoreCount()
    .then((resp) => {
      const {
        data: {
          data: {
            shopeeCountData: shopeeRegionCountsMap,
            lazadaCountData: lazadaRegionCountsMap,
            tokopediaCountData: tokopediaRegionCountsMap,
            tiktokCountData: tiktokRegionCountsMap,
            aliExpressCountData: aliexpressRegionCountsMap = {} as RegionCountsMap,
            tiktokUsCountData: tiktokUSRegionCountsMap = {} as RegionCountsMap,
            mercadoCountData: mercadoRegionCountsMap = {} as RegionCountsMap,
            kwaiCountData: kwaiRegionCountsMap = {} as RegionCountsMap,
            facebookCountData: facebookRegionCountsMap = {} as RegionCountsMap,
          },
        },
      } = resp;

      const { totalAuth: shopeeTotalAuthed, countArray: shopeeCounts } =
        getPlatformRegionCountInfo(shopeeRegionCountsMap);
      const { totalAuth: lazadaTotalAuthed, countArray: lazadaCounts } =
        getPlatformRegionCountInfo(lazadaRegionCountsMap);
      const { totalAuth: tokopediaTotalAuthed, countArray: tokopediaCounts } =
        getPlatformRegionCountInfo(tokopediaRegionCountsMap);
      const { totalAuth: tiktokTotalAuthed, countArray: tiktokCounts } =
        getPlatformRegionCountInfo(tiktokRegionCountsMap);
      const { totalAuth: aliexpressTotalAuthed, countArray: aliexpressCounts } =
        getPlatformRegionCountInfo(aliexpressRegionCountsMap);
      const { totalAuth: tiktokUSTotalAuthed, countArray: tiktokUSCounts } =
        getPlatformRegionCountInfo(tiktokUSRegionCountsMap);
      const { totalAuth: mercadoTotalAuthed, countArray: mercadoCounts } =
        getPlatformRegionCountInfo(mercadoRegionCountsMap);
      const { totalAuth: kwaiTotalAuthed, countArray: kwaiCounts } =
        getPlatformRegionCountInfo(kwaiRegionCountsMap);
      const { totalAuth: facebookTotalAuthed, countArray: facebookCounts } =
        getPlatformRegionCountInfo(facebookRegionCountsMap);

      const state: AllPlatformRegionStoreCountState = {
        platforms: {
          SHOPEE: {
            map: shopeeRegionCountsMap,
            array: shopeeCounts,
            total: shopeeTotalAuthed,
          },
          LAZADA: {
            map: lazadaRegionCountsMap,
            array: lazadaCounts,
            total: lazadaTotalAuthed,
          },
          TOKOPEDIA: {
            map: tokopediaRegionCountsMap,
            array: tokopediaCounts,
            total: tokopediaTotalAuthed,
          },
          TIKTOK: {
            map: tiktokRegionCountsMap,
            array: tiktokCounts,
            total: tiktokTotalAuthed,
          },
          ALIEXPRESS: {
            map: aliexpressRegionCountsMap,
            array: aliexpressCounts,
            total: aliexpressTotalAuthed,
          },
          TIKTOK_US: {
            map: tiktokUSRegionCountsMap,
            array: tiktokUSCounts,
            total: tiktokUSTotalAuthed,
          },
          MERCADO: {
            map: mercadoRegionCountsMap,
            array: mercadoCounts,
            total: mercadoTotalAuthed,
          },
          KWAI: {
            map: kwaiRegionCountsMap,
            array: kwaiCounts,
            total: kwaiTotalAuthed,
          },
          FACEBOOK: {
            map: facebookRegionCountsMap,
            array: facebookCounts,
            total: facebookTotalAuthed,
          },
        },
        totalStoresCount: lazadaTotalAuthed + shopeeTotalAuthed,
      };
      return state;
    })
    .catch((e) => {
      errorToasterWithServerMsg(e, '获取各区域已授权店铺数据失败');
      return Promise.resolve(lastState || { ...defaultState });
    });

const allPlatformRegionStoreCountState = atomWithRefresh(async () => {
  const rsp = await getAllRegionCounts();
  return rsp;
});

const regionStoreCountOfPlatformState = atomFamily((platform: General.PlatformUpperCase) =>
  atom((get) => {
    const rsp = get(loadable(allPlatformRegionStoreCountState));
    return rsp.state === 'hasData'
      ? rsp.data.platforms[platform]
      : defaultState.platforms[platform];
  }),
);

export const useRegionStoreCountOfPlatform = (platform: General.PlatformUpperCase) =>
  useAtomValue(regionStoreCountOfPlatformState(platform));

export const useRegionCountsRefresher = () => useSetAtom(allPlatformRegionStoreCountState);
